import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
	createAdminVocabCard,
	deleteAdminVocabCard,
	generateAdminVocabCardAudio,
	getAdminVocabCardById,
	updateAdminVocabCard,
	type UpsertVocabCardPayload,
	type VocabCardAdminItem,
	type VocabCardCefr,
	type VocabCardLang,
} from '../../../../api/vocabcard';
import { listCategories } from '../../../../api/categories';

type CategoryOption = {
	id: number;
	title: string;
};

type FormState = {
	source_lang: VocabCardLang;
	target_lang: VocabCardLang;
	term: string;
	translation: string;
	definition: string;
	definition_translation: string;
	example_sentence: string;
	example_translation: string;
	audio_path: string;
	audio_duration_seconds: string;
	image_url: string;
	cefr_level: '' | VocabCardCefr;
	is_active: boolean;
	category_ids: number[];
};

const EMPTY_FORM: FormState = {
	source_lang: 'en',
	target_lang: 'pl',
	term: '',
	translation: '',
	definition: '',
	definition_translation: '',
	example_sentence: '',
	example_translation: '',
	audio_path: '',
	audio_duration_seconds: '',
	image_url: '',
	cefr_level: '',
	is_active: true,
	category_ids: [],
};

const LANG_OPTIONS: VocabCardLang[] = ['en', 'de', 'uk', 'it', 'fr', 'es', 'pl'];
const TARGET_LANG_OPTIONS: VocabCardLang[] = ['pl', 'en', 'de', 'uk', 'it', 'fr', 'es'];
const CEFR_OPTIONS: VocabCardCefr[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

function Dialog({
	open,
	title,
	message,
	variant = 'ok',
	onClose,
}: {
	open: boolean;
	title?: string;
	message?: string;
	variant?: 'ok' | 'error';
	onClose: () => void;
}) {
	if (!open) return null;

	return (
		<div className="modal-backdrop">
			<div className={`modal-card ${variant === 'error' ? 'modal-error' : ''}`}>
				{title ? <h3>{title}</h3> : null}
				{message ? <p style={{ whiteSpace: 'pre-wrap' }}>{message}</p> : null}
				<div className="modal-actions">
					<button className="btn-primary" onClick={onClose}>OK</button>
				</div>
			</div>
		</div>
	);
}

function ConfirmDialog({
	open,
	title,
	message,
	confirmLabel = 'Potwierdź',
	cancelLabel = 'Anuluj',
	danger = false,
	loading = false,
	onConfirm,
	onCancel,
}: {
	open: boolean;
	title?: string;
	message?: string;
	confirmLabel?: string;
	cancelLabel?: string;
	danger?: boolean;
	loading?: boolean;
	onConfirm: () => void;
	onCancel: () => void;
}) {
	if (!open) return null;

	return (
		<div className="modal-backdrop">
			<div className="modal-card">
				{title ? <h3>{title}</h3> : null}
				{message ? <p style={{ whiteSpace: 'pre-wrap' }}>{message}</p> : null}
				<div className="modal-actions">
					<button className="btn-ghost" onClick={onCancel} disabled={loading}>{cancelLabel}</button>
					<button className={danger ? 'delete-btn' : 'btn-primary'} onClick={onConfirm} disabled={loading}>
						{loading ? 'Proszę czekać…' : confirmLabel}
					</button>
				</div>
			</div>
		</div>
	);
}

function LockIcon({ locked }: { locked: boolean }) {
	return locked ? (
		<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<rect x="4" y="10" width="16" height="10" rx="2"></rect>
			<path d="M8 10V7a4 4 0 0 1 8 0v3"></path>
		</svg>
	) : (
		<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<rect x="4" y="11" width="16" height="9" rx="2"></rect>
			<path d="M8 11V7a4 4 0 0 1 7.5-2"></path>
		</svg>
	);
}

export default function VocabCard() {
	const { id } = useParams();
	const navigate = useNavigate();

	const cardId = id ? Number(id) : null;
	const isEdit = Number.isInteger(cardId) && !!cardId;

	const [form, setForm] = useState<FormState>(EMPTY_FORM);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [audioLoading, setAudioLoading] = useState(false);
	const [deleteLoading, setDeleteLoading] = useState(false);

	const [locked, setLocked] = useState<boolean>(!!isEdit);

	const [cardMeta, setCardMeta] = useState<VocabCardAdminItem | null>(null);
	const [categories, setCategories] = useState<CategoryOption[]>([]);

	const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');

	const [dialogOpen, setDialogOpen] = useState(false);
	const [dialogTitle, setDialogTitle] = useState<string | undefined>();
	const [dialogMsg, setDialogMsg] = useState('');
	const [dialogVariant, setDialogVariant] = useState<'ok' | 'error'>('ok');

	const [deleteConfirm, setDeleteConfirm] = useState(false);

	const showOk = (msg: string, title = 'Sukces') => {
		setDialogVariant('ok');
		setDialogTitle(title);
		setDialogMsg(msg);
		setDialogOpen(true);
	};

	const showErr = (msg: string, title = 'Błąd') => {
		setDialogVariant('error');
		setDialogTitle(title);
		setDialogMsg(msg);
		setDialogOpen(true);
	};

	const loadCategories = async () => {
		try {
			const data = await listCategories();
			const mapped = (Array.isArray(data) ? data : []).map((c: any) => ({
				id: c.id,
				title: c.title,
			}));
			setCategories(mapped);
		} catch {
			setCategories([]);
		}
	};

	const loadCard = async (targetId: number) => {
		setLoading(true);
		try {
			const data = await getAdminVocabCardById(targetId);
			setCardMeta(data);

			setForm({
				source_lang: data.source_lang,
				target_lang: data.target_lang,
				term: data.term || '',
				translation: data.translation || '',
				definition: data.definition || '',
				definition_translation: data.definition_translation || '',
				example_sentence: data.example_sentence || '',
				example_translation: data.example_translation || '',
				audio_path: data.audio_path || '',
				audio_duration_seconds:
					data.audio_duration_seconds != null ? String(data.audio_duration_seconds) : '',
				image_url: data.image_url || '',
				cefr_level: data.cefr_level || '',
				is_active: !!data.is_active,
				category_ids: Array.isArray(data.categories) ? data.categories.map((c) => c.id) : [],
			});
		} catch (e: any) {
			showErr(e?.data?.error || e?.message || 'Nie udało się pobrać słówka.');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadCategories();
	}, []);

	useEffect(() => {
		if (isEdit && cardId) {
			loadCard(cardId);
		}
	}, [isEdit, cardId]);

	const selectedCategories = useMemo(
		() => categories.filter((c) => form.category_ids.includes(c.id)),
		[categories, form.category_ids]
	);

	const availableCategories = useMemo(
		() => categories.filter((c) => !form.category_ids.includes(c.id)),
		[categories, form.category_ids]
	);

	const inputProps = locked ? { disabled: true } : {};

	const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
		setForm((prev) => ({ ...prev, [key]: value }));
	};

	const addCategory = () => {
		const idNum = Number(selectedCategoryId);
		if (!idNum || form.category_ids.includes(idNum)) return;

		setForm((prev) => ({
			...prev,
			category_ids: [...prev.category_ids, idNum],
		}));
		setSelectedCategoryId('');
	};

	const removeCategory = (idToRemove: number) => {
		setForm((prev) => ({
			...prev,
			category_ids: prev.category_ids.filter((id) => id !== idToRemove),
		}));
	};

	const buildPayload = (): UpsertVocabCardPayload => {
		return {
			source_lang: form.source_lang,
			target_lang: form.target_lang,
			term: form.term.trim(),
			translation: form.translation.trim(),
			definition: form.definition.trim() || null,
			definition_translation: form.definition_translation.trim() || null,
			example_sentence: form.example_sentence.trim() || null,
			example_translation: form.example_translation.trim() || null,
			audio_path: form.audio_path.trim() || null,
			audio_duration_seconds: form.audio_duration_seconds
				? Number(form.audio_duration_seconds)
				: null,
			image_url: form.image_url.trim() || null,
			cefr_level: form.cefr_level || null,
			is_active: !!form.is_active,
			category_ids: form.category_ids,
		};
	};

	const validate = () => {
		if (!form.term.trim()) {
			showErr('Pole "term" jest wymagane.');
			return false;
		}
		if (!form.translation.trim()) {
			showErr('Pole "translation" jest wymagane.');
			return false;
		}
		if (
			form.audio_duration_seconds &&
			(!Number.isFinite(Number(form.audio_duration_seconds)) ||
				Number(form.audio_duration_seconds) < 0)
		) {
			showErr('audio_duration_seconds musi być poprawną liczbą.');
			return false;
		}
		return true;
	};

	const handleCreate = async () => {
		if (!validate()) return;

		setSaving(true);
		try {
			const created = await createAdminVocabCard(buildPayload());
			showOk('Słówko zostało utworzone.');
			navigate(`/admin/vocab-cards/edit/${created.id}`);
		} catch (e: any) {
			showErr(e?.data?.error || e?.message || 'Nie udało się utworzyć słówka.');
		} finally {
			setSaving(false);
		}
	};

	const handleUpdate = async () => {
		if (!cardId || !validate()) return;

		setSaving(true);
		try {
			const updated = await updateAdminVocabCard(cardId, buildPayload());
			setCardMeta(updated);
			setForm({
				source_lang: updated.source_lang,
				target_lang: updated.target_lang,
				term: updated.term || '',
				translation: updated.translation || '',
				definition: updated.definition || '',
				definition_translation: updated.definition_translation || '',
				example_sentence: updated.example_sentence || '',
				example_translation: updated.example_translation || '',
				audio_path: updated.audio_path || '',
				audio_duration_seconds:
					updated.audio_duration_seconds != null ? String(updated.audio_duration_seconds) : '',
				image_url: updated.image_url || '',
				cefr_level: updated.cefr_level || '',
				is_active: !!updated.is_active,
				category_ids: Array.isArray(updated.categories) ? updated.categories.map((c) => c.id) : [],
			});
			showOk('Zmiany zapisane.');
		} catch (e: any) {
			showErr(e?.data?.error || e?.message || 'Nie udało się zapisać zmian.');
		} finally {
			setSaving(false);
		}
	};

	const handleGenerateAudio = async () => {
		if (!cardId) return;

		setAudioLoading(true);
		try {
			const res = await generateAdminVocabCardAudio(cardId);
			setForm((prev) => ({
				...prev,
				audio_path: res.audio?.audio_path || '',
				audio_duration_seconds:
					res.audio?.audio_duration_seconds != null
						? String(res.audio.audio_duration_seconds)
						: '',
			}));
			if (cardId) {
				await loadCard(cardId);
			}
			showOk('Audio zostało wygenerowane.');
		} catch (e: any) {
			showErr(e?.data?.error || e?.message || 'Nie udało się wygenerować audio.');
		} finally {
			setAudioLoading(false);
		}
	};

	const handleDelete = async () => {
		if (!cardId) return;

		setDeleteLoading(true);
		try {
			await deleteAdminVocabCard(cardId);
			setDeleteConfirm(false);
			showOk('Słówko zostało usunięte.');
			navigate('/admin/vocab-cards');
		} catch (e: any) {
			showErr(e?.data?.error || e?.message || 'Nie udało się usunąć słówka.');
		} finally {
			setDeleteLoading(false);
		}
	};

	return (
		<>
			<div className="contents-add-container">
				<div className="breadcrumb">
					<span onClick={() => navigate('/admin')} className="breadcrumb-link">Dashboard</span> /
					<span onClick={() => navigate('/admin/vocab-cards')} className="breadcrumb-link">Słówka</span> /
					<span>{isEdit ? ` Edytuj słówko / ${cardId}` : 'Dodaj słówko'}</span>
				</div>

				<div className="title-bar">
					<h2>{isEdit ? 'Edytuj słówko' : 'Dodaj słówko'}</h2>

					<button
						type="button"
						className="btn-ghost"
						onClick={() => navigate('/admin/vocab-cards?import=1')}
					>
						Import JSON
					</button>

					<div className="edit-buttons">
						<div className="form-buttons">
							{!isEdit ? (
								<button type="button" onClick={handleCreate} disabled={saving || loading}>
									{saving ? 'Tworzę…' : 'Utwórz słówko'}
								</button>
							) : (
								<>
									<button type="button" onClick={handleUpdate} disabled={saving || loading || locked}>
										{saving ? 'Zapisuję…' : 'Zapisz zmiany'}
									</button>

									<button
										type="button"
										className="btn-ghost"
										onClick={handleGenerateAudio}
										disabled={audioLoading || loading || locked || !cardId || !form.term.trim()}
									>
										{audioLoading ? 'Generuję audio…' : 'Generuj audio'}
									</button>

									<button
										type="button"
										className="delete-btn"
										onClick={() => setDeleteConfirm(true)}
										disabled={deleteLoading || loading}
									>
										Usuń
									</button>
								</>
							)}
						</div>

						{isEdit ? (
							<button
								onClick={() => setLocked((prev) => !prev)}
								className={`lock-toggle ${locked ? 'is-locked' : 'is-unlocked'}`}
								title={locked ? 'Odblokuj edycję' : 'Zablokuj edycję'}
								aria-label={locked ? 'Odblokuj edycję' : 'Zablokuj edycję'}
								type="button"
							>
								<LockIcon locked={locked} />
							</button>
						) : null}
					</div>
				</div>

				<div className="contents-add-wrapper">
					<div className="form-column">
						<div className="add-content-form">
							<label>
								Term *
								<input
									value={form.term}
									onChange={(e) => setField('term', e.target.value)}
									placeholder="np. launch"
									{...inputProps}
								/>
							</label>

							<label>
								Tłumaczenie *
								<input
									value={form.translation}
									onChange={(e) => setField('translation', e.target.value)}
									placeholder="np. odpalić / wystartować"
									{...inputProps}
								/>
							</label>

							<label>
								Język źródłowy
								<select
									value={form.source_lang}
									onChange={(e) => setField('source_lang', e.target.value as VocabCardLang)}
									{...inputProps}
								>
									{LANG_OPTIONS.map((lang) => (
										<option key={lang} value={lang}>
											{lang.toUpperCase()}
										</option>
									))}
								</select>
							</label>

							<label>
								Język docelowy
								<select
									value={form.target_lang}
									onChange={(e) => setField('target_lang', e.target.value as VocabCardLang)}
									{...inputProps}
								>
									{TARGET_LANG_OPTIONS.map((lang) => (
										<option key={lang} value={lang}>
											{lang.toUpperCase()}
										</option>
									))}
								</select>
							</label>

							<label>
								Poziom CEFR
								<select
									value={form.cefr_level}
									onChange={(e) => setField('cefr_level', e.target.value as '' | VocabCardCefr)}
									{...inputProps}
								>
									<option value="">— brak —</option>
									{CEFR_OPTIONS.map((level) => (
										<option key={level} value={level}>
											{level}
										</option>
									))}
								</select>
							</label>

							<label>
								Definicja
								<textarea
									value={form.definition}
									onChange={(e) => setField('definition', e.target.value)}
									placeholder="np. to start or send off something new..."
									{...inputProps}
								/>
							</label>

							<label>
								Tłumaczenie definicji
								<textarea
									value={form.definition_translation}
									onChange={(e) => setField('definition_translation', e.target.value)}
									placeholder="np. rozpocząć lub wystartować coś nowego..."
									{...inputProps}
								/>
							</label>

							<label>
								Zdanie przykładowe
								<textarea
									value={form.example_sentence}
									onChange={(e) => setField('example_sentence', e.target.value)}
									placeholder="np. We will launch the product next month."
									{...inputProps}
								/>
							</label>

							<label>
								Tłumaczenie zdania
								<textarea
									value={form.example_translation}
									onChange={(e) => setField('example_translation', e.target.value)}
									placeholder="np. Wypuścimy produkt w przyszłym miesiącu."
									{...inputProps}
								/>
							</label>
						</div>
					</div>

					<div className="media-column">
						<div className="upload-section">
							<h3>Media i techniczne</h3>

							<label>
								URL obrazka
								<input
									value={form.image_url}
									onChange={(e) => setField('image_url', e.target.value)}
									placeholder="https://..."
									{...inputProps}
								/>
							</label>

							<label>
								Ścieżka audio
								<input
									value={form.audio_path}
									onChange={(e) => setField('audio_path', e.target.value)}
									placeholder="np. vocab_cards/12/en/en.mp3"
									{...inputProps}
								/>
							</label>

							<label>
								Długość audio (sekundy)
								<input
									type="number"
									min={0}
									value={form.audio_duration_seconds}
									onChange={(e) => setField('audio_duration_seconds', e.target.value)}
									placeholder="np. 3"
									{...inputProps}
								/>
							</label>

							<div className="toggle-row">
								<label className={`violet-toggle ${locked ? 'is-disabled' : ''}`}>
									<input
										type="checkbox"
										checked={form.is_active}
										onChange={(e) => setField('is_active', e.target.checked)}
										disabled={locked}
									/>
									<span className="vt-track" aria-hidden="true"><span className="vt-thumb" /></span>
									<span className="vt-text">
										Aktywne <span className="vt-state">{form.is_active ? 'TAK' : 'NIE'}</span>
									</span>
								</label>
							</div>

							<div className="chips-section">
								<div className="chips-title">Kategorie</div>

								{selectedCategories.length > 0 ? (
									<ul className="chip-list">
										{selectedCategories.map((cat) => (
											<li key={cat.id} className="chip">
												{cat.title}
												{!locked && (
													<button
														type="button"
														className="chip-remove"
														onClick={() => removeCategory(cat.id)}
														aria-label={`Usuń kategorię ${cat.title}`}
														title="Usuń"
													>
														×
													</button>
												)}
											</li>
										))}
									</ul>
								) : (
									<div className="add-info" style={{ marginTop: 8 }}>
										Brak przypisanych kategorii.
									</div>
								)}
							</div>

							<div className="autocomplete-wrapper">
								<select
									value={selectedCategoryId}
									onChange={(e) => setSelectedCategoryId(e.target.value)}
									disabled={locked || availableCategories.length === 0}
								>
									<option value="">Dodaj kategorię…</option>
									{availableCategories.map((cat) => (
										<option key={cat.id} value={String(cat.id)}>
											{cat.title}
										</option>
									))}
								</select>

								<button
									type="button"
									className="btn-ghost"
									onClick={addCategory}
									disabled={locked || !selectedCategoryId}
									style={{ marginTop: 8 }}
								>
									Dodaj kategorię
								</button>
							</div>
						</div>

						{isEdit && cardMeta ? (
							<div className="upload-section">
								<h3>Metadane</h3>
								<div className="add-info">ID: {cardMeta.id}</div>
								<div className="add-info">Polubienia: {cardMeta.like_count ?? 0}</div>
								<div className="add-info">
									Utworzono: {cardMeta.created_at ? new Date(cardMeta.created_at).toLocaleString() : '—'}
								</div>
								<div className="add-info">
									Ostatnia edycja: {cardMeta.updated_at ? new Date(cardMeta.updated_at).toLocaleString() : '—'}
								</div>
							</div>
						) : null}
					</div>
				</div>
			</div>

			<Dialog
				open={dialogOpen}
				title={dialogTitle}
				message={dialogMsg}
				variant={dialogVariant}
				onClose={() => setDialogOpen(false)}
			/>

			<ConfirmDialog
				open={deleteConfirm}
				title="Usunąć słówko?"
				message={`Czy na pewno chcesz usunąć „${form.term || `#${cardId}`}”?`}
				confirmLabel="Usuń"
				cancelLabel="Anuluj"
				danger
				loading={deleteLoading}
				onCancel={() => setDeleteConfirm(false)}
				onConfirm={handleDelete}
			/>
		</>
	);
}